'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import type { StepWithTranslation, IngredientWithDetails } from '@/lib/data/recipes';
import { stepIngredientAmount } from '@/lib/utils/placeholders';
import { resolveInstructionNodes } from '@/lib/utils/instruction-nodes';
import type { UnitSystem } from '@/lib/utils/unit-display';
import { joinAmount } from '@/lib/utils/format-amount';
import { getIngredientThumbImageUrl } from '@/lib/utils/image-url';
import { ThumbImage } from '@/components/ui/thumb-image';
import { TimelineDivider } from '@/components/ui/timeline-divider';
import { NotePanel } from '@/components/ui/note-panel';
import { groupStepsByPhase, maxAdvanceDays } from '@/lib/recipes/step-phases';
import styles from './step-list.module.css';

interface StepListProps {
  steps: StepWithTranslation[];
  ingredients: IngredientWithDetails[];
  selectedServings: number;
  baseServings: number;
  locale?: 'en' | 'de';
  unitSystem?: UnitSystem;
}

export function StepList({
  steps,
  ingredients,
  selectedServings,
  baseServings,
  locale = 'en',
  unitSystem = 'metric',
}: StepListProps) {
  const t = useTranslations('recipes');
  // Tap-to-spotlight: clicking a step focuses it and fades the rest. Purely a
  // pointer reading-aid — no step content is gated behind it and the steps keep
  // their list semantics, so screen readers read every step normally.
  const [focusedId, setFocusedId] = useState<string | null>(null);

  if (steps.length === 0) {
    return <p className={styles.empty}>{t('stepsEmpty')}</p>;
  }

  // Build a map of sort_order → ingredient for the placeholder resolver
  const ingredientMap = ingredients.map((ing) => ({
    sortOrder: ing.sortOrder,
    displayQuantity: ing.displayQuantity,
    displayUnit: ing.displayUnitAbbrev,
    name: ing.name,
  }));

  // Phase heading. 0 is the day of the meal; everything else counts back from
  // it. Two named days and then a generic count — the count carries any lead
  // time a long cure or ferment produces without a per-value string (§19.7).
  const phaseLabel = (advanceDays: number) => {
    if (advanceDays === 0) return t('stepsPhaseDayOf');
    if (advanceDays === 1) return t('stepsPhaseDayBefore');
    return t('stepsPhaseDaysAhead', { days: advanceDays });
  };

  // Day phases. A recipe with no day-ahead work yields exactly one phase and
  // renders as the flat list it always did — no divider, no notice (#919).
  const phases = groupStepsByPhase(steps);
  const leadDays = maxAdvanceDays(steps);

  const renderStep = (step: StepWithTranslation) => {
    const instructionNodes = resolveInstructionNodes(
      step.instructionText,
      ingredientMap,
      selectedServings,
      baseServings,
      locale,
      unitSystem,
      styles.ingHighlight,
    );

    // Get the ingredients referenced in this step
    const stepIngredients = step.ingredientRefs
      .map((ref) => ingredients.find((ing) => ing.sortOrder === ref))
      .filter((ing): ing is IngredientWithDetails => ing !== undefined);

    const isFocused = focusedId === step.id;

    return (
      <li
        key={step.id}
        className={styles.step}
        data-focused={isFocused ? 'true' : undefined}
        // Decorative spotlight — mouse/touch only; all content stays readable.

        onClick={() => setFocusedId((cur) => (cur === step.id ? null : step.id))}
      >
        <div className={styles.stepNumber} aria-hidden="true">
          {step.stepNumber}
        </div>

        <div className={styles.stepContent}>
          {step.title && <h3 className={styles.stepTitle}>{step.title}</h3>}

          {/* Ingredient strip — shown above instruction */}
          {stepIngredients.length > 0 && (
            <div className={styles.ingredientStrip} aria-label={t('ingredients')}>
              {stepIngredients.map((ing) => {
                // Step-specific portion (not the recipe total): {{qty:N}} means
                // the whole amount, {{portion:N|amt|unit}} a share; null when the
                // step only names the ingredient without a quantity.
                const amount = stepIngredientAmount(
                  step.instructionText,
                  {
                    sortOrder: ing.sortOrder,
                    displayQuantity: ing.displayQuantity,
                    displayUnit: ing.displayUnitAbbrev,
                    name: ing.name,
                  },
                  selectedServings,
                  baseServings,
                  locale,
                  unitSystem,
                );

                const hasImage =
                  ing.ingredientImageUrl != null && ing.ingredientImageStatus === 'generated';

                return (
                  <span key={ing.id} className={styles.stripIngredient}>
                    {hasImage && (
                      <ThumbImage
                        thumbSrc={getIngredientThumbImageUrl(ing.ingredientImageUrl)!}
                        fallbackSrc={ing.ingredientImageUrl!}
                        alt=""
                        width={24}
                        height={24}
                        className={styles.stripImage}
                      />
                    )}
                    {amount && (
                      <span className={styles.stripQty}>{joinAmount(amount.qty, amount.unit)}</span>
                    )}
                    <span className={styles.stripName}>{ing.name}</span>
                  </span>
                );
              })}
            </div>
          )}

          <p className={styles.instruction}>{instructionNodes}</p>
        </div>
      </li>
    );
  };

  const listProps = {
    className: styles.list,
    'data-has-focus': focusedId != null ? 'true' : undefined,
  } as const;

  // Single phase — the shape this component had before day grouping existed.
  if (phases.length === 1) {
    return (
      <ol {...listProps} aria-label={t('steps')}>
        {phases[0].steps.map(renderStep)}
      </ol>
    );
  }

  return (
    <div className={styles.phases}>
      {/* Lead time is a commitment the cook makes BEFORE starting, so it is
          stated above the steps rather than discovered at the step that needs
          it. */}
      <NotePanel label={t('stepsAdvanceLabel')} ariaLabel={t('stepsAdvanceLabel')}>
        {t('stepsAdvanceNotice', { days: leadDays })}
      </NotePanel>

      {phases.map((phase, index) => {
        const headingId = `step-phase-${index}`;
        return (
          <section key={headingId} className={styles.phase} aria-labelledby={headingId}>
            <TimelineDivider
              id={headingId}
              label={phaseLabel(phase.advanceDays)}
              active={phase.advanceDays === 0}
            />
            <ol {...listProps}>{phase.steps.map(renderStep)}</ol>
          </section>
        );
      })}
    </div>
  );
}
